import type { AudioEngine } from '../audio/engine';
import { concat, peakOf, tidy, type StereoAudio } from './audio-data';

export const MAX_SECONDS = 60;
const TAIL_MIN = 2.5;
const TAIL_CAP = 10;
const QUIET_PEAK = 0.002;
const QUIET_SECONDS = 0.3;

export type CaptureState = 'idle' | 'recording' | 'tail';

interface Handlers {
  onState(state: CaptureState): void;
  onTick(elapsed: number): void;
  /** Fired once when the one-minute limit ends a recording. */
  onLimit(): void;
  /** How long a release tail may ring after stopping (e.g. the reverb decay). */
  tailSeconds(): number;
  /** `audio` is null when nothing audible was played. */
  onDone(audio: StereoAudio | null): void;
}

/**
 * Records the master output losslessly. When stopped it keeps listening
 * while the sound rings out (up to the reverb's decay, at most 10 s) so
 * tails end naturally instead of being chopped off.
 */
export class Capture {
  state: CaptureState = 'idle';
  private left: Float32Array[] = [];
  private right: Float32Array[] = [];
  private startedAt = 0;
  private quietFrames = 0;
  private tailFrames = 0;
  private recordedFrames = 0;
  private tailMax = TAIL_MIN;
  private stopSent = false;
  private fallback = 0;
  private raf = 0;

  constructor(
    private engine: AudioEngine,
    private h: Handlers,
  ) {}

  private get port(): MessagePort | null {
    return this.engine.recorderNode?.port ?? null;
  }

  start(): boolean {
    const port = this.port;
    if (!port || this.state !== 'idle') return false;
    this.left = [];
    this.right = [];
    this.quietFrames = 0;
    this.tailFrames = 0;
    this.recordedFrames = 0;
    this.stopSent = false;
    port.onmessage = (e) => this.onMessage(e.data);
    port.postMessage('start');
    this.startedAt = this.engine.ctx.currentTime;
    this.setState('recording');
    const tick = () => {
      if (this.state !== 'recording') return;
      const elapsed = this.engine.ctx.currentTime - this.startedAt;
      this.h.onTick(Math.min(elapsed, MAX_SECONDS));
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
    return true;
  }

  stop(): void {
    if (this.state !== 'recording') return;
    cancelAnimationFrame(this.raf);
    this.tailMax = Math.min(TAIL_CAP, Math.max(TAIL_MIN, this.h.tailSeconds()));
    this.setState('tail');
    // If the audio clock stalls (e.g. the context gets suspended), finish
    // with what we have instead of waiting forever.
    clearTimeout(this.fallback);
    this.fallback = window.setTimeout(() => {
      if (this.state === 'tail') this.finish();
    }, (this.tailMax + 2) * 1000);
  }

  private onMessage(msg: { type: string; l?: Float32Array; r?: Float32Array }): void {
    if (this.state === 'idle') return;
    if (msg.type === 'chunk' && msg.l && msg.r) {
      this.left.push(msg.l);
      this.right.push(msg.r);
      const n = msg.l.length;
      const sr = this.engine.ctx.sampleRate;
      if (this.state === 'recording') {
        // Counted in audio frames, so the limit holds even in a background
        // tab where animation frames pause.
        this.recordedFrames += n;
        if (this.recordedFrames >= MAX_SECONDS * sr) {
          this.h.onLimit();
          this.stop();
        }
      } else if (this.state === 'tail' && !this.stopSent) {
        this.tailFrames += n;
        this.quietFrames = peakOf(msg.l, msg.r) < QUIET_PEAK ? this.quietFrames + n : 0;
        if (this.quietFrames >= QUIET_SECONDS * sr || this.tailFrames >= this.tailMax * sr) {
          this.stopSent = true;
          this.port?.postMessage('stop');
        }
      }
    } else if (msg.type === 'stopped' && this.state === 'tail') {
      this.finish();
    }
  }

  private finish(): void {
    clearTimeout(this.fallback);
    if (!this.stopSent) this.port?.postMessage('stop');
    this.stopSent = true;
    const raw: StereoAudio = {
      sampleRate: this.engine.ctx.sampleRate,
      left: concat(this.left),
      right: concat(this.right),
    };
    this.left = [];
    this.right = [];
    this.setState('idle');
    this.h.onDone(peakOf(raw.left, raw.right) < QUIET_PEAK ? null : tidy(raw));
  }

  private setState(s: CaptureState): void {
    this.state = s;
    this.h.onState(s);
  }
}
